import { EllipsedText, FlexColumn, LabeledData } from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { StyledDivider } from 'baseui/divider';
import { ParagraphSmall } from 'baseui/typography';
import { useMemo, useState } from 'react';
import { ASSET_TYPE, ASSET_TYPE_CONTAINER } from '../../constants/asset';
import {
  BULK_ASSETS_FILE,
  BULK_CONTAINER_ASSETS_TABLE_COLUMNS,
  BULK_DEVICES_FILE,
  BULK_DEVICES_TABLE_COLUMNS,
  BULK_DISABLE_MONITORING_FILE,
  BULK_DISABLE_MONITORING_TABLE_COLUMNS,
  BULK_FILE_TYPES,
  BULK_LINK_DEVICE_ASSETS_FILE,
  BULK_LINK_DEVICE_ASSETS_TABLE_COLUMNS,
  BULK_VEHICLE_ASSETS_TABLE_COLUMNS,
} from '../../constants/bulk';
import { useStyles } from '../../hooks/useStyles';
import { Bulk } from '../../types/bulk';
import { isVehicleLikeType } from '../../utils/assets';
import { StyledPaginatedTable } from '../shared/StyledPaginatedTable';

type Props = {
  bulkFile: Bulk;
};
export function BulkInfo({ bulkFile }: Readonly<Props>) {
  const { theme } = useStyles();
  const [page, setPage] = useState(1);
  const labelWidth = '160px';
  const isVehicleLike = isVehicleLikeType(bulkFile.metadata.assettype);

  const bulkRowsColumnNames = (() => {
    switch (bulkFile.metadata.filetype) {
      case BULK_LINK_DEVICE_ASSETS_FILE:
        return BULK_LINK_DEVICE_ASSETS_TABLE_COLUMNS;
      case BULK_ASSETS_FILE:
        if (isVehicleLike) return BULK_VEHICLE_ASSETS_TABLE_COLUMNS;
        else if (bulkFile.metadata.assettype === ASSET_TYPE_CONTAINER) return BULK_CONTAINER_ASSETS_TABLE_COLUMNS;
        return [];
      case BULK_DEVICES_FILE:
        return BULK_DEVICES_TABLE_COLUMNS;
      case BULK_DISABLE_MONITORING_FILE:
        return BULK_DISABLE_MONITORING_TABLE_COLUMNS;
      default:
        return [];
    }
  })();

  const bulkData = useMemo(() => {
    return bulkFile.rows.map((row, i) => {
      let items: any[] = [];
      switch (bulkFile.metadata.filetype) {
        case BULK_LINK_DEVICE_ASSETS_FILE:
          items = [
            <EllipsedText key={`ident-${i}`}>{row.ident?.toString() ?? '-'}</EllipsedText>,
            <EllipsedText key={`identifier-${i}`}>{row.identifier?.toString() ?? '-'}</EllipsedText>,
          ];
          break;
        case BULK_ASSETS_FILE:
          if (isVehicleLike)
            items = [
              row.licensePlate,
              <EllipsedText key={`label-${i}`}>{row.label?.toString() ?? '-'}</EllipsedText>,
              row.brand,
              row.subBrand,
              row.year,
              row.color,
              row.version,
              row.vin,
              row.vehicleType,
            ];
          else if (bulkFile.metadata.assettype === ASSET_TYPE_CONTAINER)
            items = [
              <EllipsedText key={`identifier-${i}`}>{row.identifier?.toString() ?? '-'}</EllipsedText>,
              <EllipsedText key={`label-${i}`}>{row.label?.toString() ?? '-'}</EllipsedText>,
              <EllipsedText key={`ident-${i}`}>{row.ident?.toString() ?? '-'}</EllipsedText>,
            ];
          break;
        case BULK_DEVICES_FILE:
          items = [
            <EllipsedText key={`ident-${i}`}>{row.ident?.toString() ?? '-'}</EllipsedText>,
            row.imei,
            row.sim,
            row.carrier,
            row.maxMb?.toString() ?? '-',
          ];
          break;
        case BULK_DISABLE_MONITORING_FILE:
          items = [<EllipsedText key={`ident-${i}`}>{row.ident?.toString() ?? '-'}</EllipsedText>];
          break;
      }
      items.push(
        <ParagraphSmall
          key={`errors-${i}`}
          margin={0}
          color={theme.colors.negative}
        >
          {row.error}
        </ParagraphSmall>,
      );
      return {
        items,
      };
    });
  }, [bulkFile.metadata.assettype, bulkFile.metadata.filetype, bulkFile.rows]);

  return (
    <>
      <FlexColumn gap={theme.sizing.scale500}>
        <LabeledData
          label="Tipo de archivo:"
          content={BULK_FILE_TYPES[bulkFile.metadata.filetype] ?? 'No válido'}
          labelWidth={labelWidth}
        />
        <FlexColumn gap={theme.sizing.scale300}>
          <LabeledData
            label="Errores:"
            content={bulkFile.errors.length?.toString()}
            labelWidth={labelWidth}
          />
          {!!bulkFile.errors.length && (
            <ParagraphSmall
              margin={0}
              color={theme.colors.negative}
            >
              {bulkFile.errors}
            </ParagraphSmall>
          )}
        </FlexColumn>
      </FlexColumn>
      <StyledDivider
        $size="cell"
        $style={{ width: '100%' }}
      />
      {bulkFile.metadata.filetype === BULK_ASSETS_FILE && (
        <FlexColumn gap={theme.sizing.scale500}>
          <LabeledData
            label="Cliente:"
            content={bulkFile.client?.name ?? 'No encontrado'}
            labelWidth={labelWidth}
          />
          <LabeledData
            label="Subcliente:"
            content={bulkFile.subClient?.name ?? 'No encontrado'}
            labelWidth={labelWidth}
          />
          <LabeledData
            label="Tipo de activo:"
            content={bulkFile.metadata.assettype ? ASSET_TYPE[bulkFile.metadata.assettype] : '-'}
            labelWidth={labelWidth}
          />
        </FlexColumn>
      )}
      {bulkFile.metadata.filetype === BULK_DEVICES_FILE && (
        <FlexColumn gap={theme.sizing.scale500}>
          <LabeledData
            label="Marca:"
            content={bulkFile.deviceBrand?.name ?? 'Sin marca'}
            labelWidth={labelWidth}
          />
          <LabeledData
            label="Modelo:"
            content={bulkFile.deviceModel?.name ?? 'Sin modelo'}
            labelWidth={labelWidth}
          />
        </FlexColumn>
      )}
      {bulkFile.metadata.filetype === BULK_DISABLE_MONITORING_FILE && (
        <FlexColumn gap={theme.sizing.scale500}>
          <LabeledData
            label="Cliente:"
            content={bulkFile.client?.name ?? 'No encontrado'}
            labelWidth={labelWidth}
          />
          <LabeledData
            label="Subcliente:"
            content={bulkFile.subClient?.name ?? 'No encontrado'}
            labelWidth={labelWidth}
          />
          {!!bulkFile.errors.length && (
            <LabeledData
              label="Campos requeridos:"
              content={bulkFile.requiredFields}
              labelWidth="labelWidth"
            />
          )}
        </FlexColumn>
      )}

      {!!bulkFile.rows.length && (
        <Block height={'240px'}>
          <StyledPaginatedTable
            columns={bulkRowsColumnNames}
            data={bulkData}
            totalItems={bulkFile.rows.length}
            page={page}
            onPageChange={setPage}
            loading={false}
            minColumnWidth={bulkFile.metadata.filetype === BULK_ASSETS_FILE && isVehicleLike ? '190px' : undefined}
          />
        </Block>
      )}
    </>
  );
}
